/**
 * ZoneForm — create/edit modal for a zone, opened from the Zones list page.
 *
 * On create, the uploaded register export (.csv/.xls/.xlsx) is parsed
 * ENTIRELY in the browser with SheetJS; the mapping in handleOnFinish is
 * where the Swedish source columns ("Fastighetsadress", "RegistreringsNummer",
 * "Ägare", ...) are translated to our Location fields — extend that mapping
 * when the export format changes. Rows with invalid coordinates are sent
 * with a dummy position; rows without coordinates are sent empty and get
 * geocoded server-side after creation.
 */
import React, { useState } from "react";
import { Form, Input, Spin, Upload } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { isEmpty, compact } from "lodash";
import * as XLSX from 'xlsx'

import "./Zone.scss";
import AddressInput1 from "../../Inputs/AddressInput1";
import { SubmitIcon } from "components/Icons";
import { PrimaryButton, TextButton } from 'components/Buttons';
import { isValidLatLng } from 'utils/validator';

const ZoneForm = ({
  form,
  isFetching,
  zone,
  handleSubmit,
  handleCancel,
}) => {
  const [state, setState] = useState({
    data: [],
  });
  const handleOnFinish = (values) => {
    // const formData = new FormData();
    // formData.append("file", state.file);
    // formData.append("name", values.name);
    // formData.append("centerAddress", values.centerAddress);
    // formData.append("distanceRadius", values.distanceRadius);
    // console.log(state);
    // console.log(values);
    // console.log(formData);

    const params = { ...values };
    if (!zone) {
      params.locations = state.data.map((d) => {
        let lat = +d["Latitud"];
        let lng = +d["Longitud"];
        
        if (!lat || !lng) {
          lat = '';
          lng = '';
        }
        else if (!isValidLatLng(lat, lng)) {
          lat = 60.55273155962545;
          lng = 18.97394158237427;
        }

        return {
          address: compact([d["Fastighetsadress"], d["Fastighet postadress"]]).map(v => v.trim()).join(", "),
          country: d["Land"],
          lat: lat,
          lng: lng,
          registrationNumber: d["RegistreringsNummer"],
          propertyDesignation: d["Fastighetsbeteckning"],
          owner: d["Ägare"],
        };
      });
    }
    handleSubmit(params);
  };

  const handleFileChange = (e) => {
    setState({ ...state, file: null, data: [], loading: true });
    const file = e.file.originFileObj;
    if (file) {
      const reader = new FileReader();
      const extension = file.name.split(".").pop().toLowerCase();
      let type;
      switch (extension) {
        case "csv":
          reader.readAsText(file, "UTF-8");
          type = "string";
          break;
        default:
          // for xlsx, xls, xltx
          reader.readAsBinaryString(file);
          type = "binary";
          break;
      }
      reader.onload = (e) => {
        const bstr = e.target.result;
        const wb = XLSX.read(bstr, { type, raw: true, cellDates: true });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws, { raw: false, defval: "" });
        setState({ ...state, file, data, loading: false });
      };
    }
  };

  return (
    <Spin spinning={isFetching}>
      <div className="ZoneForm">
        <Form
          form={form}
          scrollToFirstError
          layout="vertical"
          hideRequiredMark
          colon={false}
          onFinish={handleOnFinish}
          onKeyPress={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
            }
          }}
        >
          <div className="FormHeader">
            <div className="left">
              {zone ? "Redigera Zon" : "Ny Zon"}
            </div>
          </div>
          <div className="FormContent">
            <Form.Item
              label="Namge zon"
              name="name"
              rules={[{ required: true, message: "Required" }]}
              style={{ width: "100%" }}
            >
              <Input
                size="large"
                placeholder="Namge zon"
              />
            </Form.Item>
            
            <Form.Item
              label="Ange zonens centerpunkt "
              name="centerAddress"
              rules={[
                {
                  validator: (_, value) => {
                    if (
                      value?.address &&
                      value?.latLng?.lat &&
                      value?.latLng?.lng
                    ) {
                      return Promise.resolve();
                    }

                    if (isEmpty(value?.address)) {
                      return Promise.reject(new Error("Required"));
                    }

                    return Promise.reject(new Error("Invalid"));
                  },
                },
              ]}
            >
              <AddressInput1
                size="large"
                placeholder="Ange zonens centerpunkt "
                onSelect={(value) =>
                  form.setFieldsValue({ centerAddress: value })
                }
              />
            </Form.Item>
            <Form.Item
              label="Distans radie "
              name="distanceRadius"
              rules={[{ required: true, message: "Required" }]}
              style={{ width: "100%" }}
            >
              <Input
                size="large"
                placeholder="Distans radie "
                suffix="Km"
                type="number"
              />
            </Form.Item>
            {!zone && (
              <Form.Item
                label="Ladda upp adresser "
                name="file"
                rules={[
                  {
                    validator: (_, value) => {
                      if (isEmpty(state.file)) {
                        return Promise.reject(new Error("Required"));
                      } else {
                        return Promise.resolve();
                      }
                    },
                  },
                ]}
              >
                <Upload.Dragger
                  fileList={state.file ? [state.file] : []}
                  multiple={false}
                  accept=".csv, .xls, .xlsx, .xltx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  customRequest={() => null}
                  // showUploadList={false}
                  onChange={(e) => handleFileChange(e)}
                >
                  <p className="upload-text">Dra och släpp din fil här eller</p>
                  <p className="upload-drag-icon">
                    Bläddra
                    <UploadOutlined />
                  </p>
                  <p className="upload-supported">(stödjer format csv, xls eller xlsx)</p>
                </Upload.Dragger>
                <div className="bottom-text">
                  <div className="download-text">
                    <span>Ladda ner exempelfil: </span>
                    <a
                      target="_blank"
                      href="/files/Sample_file_Layout.xlsx?v=1.0"
                      rel="noopener noreferrer"
                    >
                      Sample_file_Layout.xlsx
                    </a>
                  </div>
                </div>
              </Form.Item>
            )}
          </div>
          <div className="FormFooter">
            <div className="Left"></div>
            <div className="Right">
              <TextButton
                key="cancel"
                label="Avbryt"
                onClick={handleCancel}
              />
              <PrimaryButton
                htmlType="submit"
                type="primary"
                label="Bekräfta"
                icon={<SubmitIcon />}
              />
            </div>
          </div>
        </Form>
      </div>
    </Spin>
  );
};

export default ZoneForm;
